import moment, { unitOfTime } from 'moment';

import { t } from '@core/i18n';
import { MaterialDate } from '@shared/components/date-picker';

import { pluralText } from './number';

export const PICKER_DATE_FORMAT = 'DD/MM/YYYY';
export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_TIME_PICKER_FORMAT = 'DD/MM/YYYY HH:mm a';

export const formatDateFrom = (
  date: moment.MomentInput,
  formatFrom: string = PICKER_DATE_FORMAT,
  formatTo: string = DEFAULT_DATE_FORMAT,
  utc = true
) => {
  if (!date) {
    return '';
  }

  const momentDate = utc ? moment.utc(date, formatFrom) : moment(date, formatFrom);
  return momentDate.isValid() ? momentDate.format(formatTo) : '';
};

/**
 * @deprecated Prefer `formatTimestamp`: it reads a bare number as unix seconds (this function
 * reads it as milliseconds, so unix-second timestamps render in 1970), and it has no `utc`
 * argument to get wrong.
 */
export const formatDate = (date: moment.MomentInput, format = 'Do MMM YYYY', utc = true) => {
  if (!date) {
    return '';
  }

  const momentDate = utc ? moment.utc(date) : moment(date);
  return momentDate.isValid() ? momentDate.format(format) : '';
};

export const formatTimeFromNow = (date: moment.MomentInput, utc = true) => {
  if (!date) {
    return '';
  }

  const momentDate = utc ? moment.utc(date) : moment(date);
  return momentDate.isValid() ? momentDate.fromNow() : '';
};

export const timeToDate = (time: string, utc = true, type?: 'AM' | 'PM') => {
  const init = utc ? moment.utc : moment;
  const args = {
    time: type ? `${time} ${type}` : time,
    format: type ? ['hh:mm A'] : ['HH:mm'],
  };

  return init(args.time, args.format).toISOString();
};

export const dateToTime = (date: string, utc = true, type?: 'AM' | 'PM') => {
  const format = type ? 'hh:mm' : 'HH:mm';

  return formatDate(date, format, utc);
};

export const isSameDay = (from?: string, to?: string): boolean => {
  if (!from || !to) {
    return false;
  }

  return formatDate(from) === formatDate(to);
};

export const isSameDate = (
  compareToDate: moment.MomentInput,
  comparableDate: moment.MomentInput,
  compareType?: unitOfTime.Base,
  utc?: boolean
) => {
  return utc
    ? moment.utc(comparableDate).isSame(compareToDate, compareType)
    : moment(comparableDate).isSame(compareToDate, compareType);
};

export const isAfter = (
  compareToDate: string,
  comparableDate: string,
  compareType?: unitOfTime.StartOf,
  utc?: boolean
) => {
  const init = utc ? moment.utc : moment;

  return init(comparableDate).isAfter(compareToDate, compareType);
};

export const getTimeDifferenceText = (dateFrom: MaterialDate, dateTo: MaterialDate) => {
  const from = moment(dateFrom);
  const to = moment(dateTo).add(1, 'days');

  const count = (label: string, count: number | string) => {
    const numberCount = Number(count);

    if (numberCount == 1) {
      return `${count} ${label} `;
    } else if (numberCount > 1) {
      return `${count} ${label}s  `;
    } else {
      return '';
    }
  };

  let years: number | string = to.diff(from, 'year');
  from.add(years, 'years');

  let months: number | string = to.diff(from, 'months');
  from.add(months, 'months');

  let days: number | string = to.diff(from, 'days');

  years = years > 0 ? count('year', years) : '';
  months = months > 0 ? count('month', months) : '';
  days = days > 0 ? count('day', days) : '';

  return years + months + days;
};

export const getQueryDate = (monthAgo?: number) => {
  if (monthAgo) {
    return moment().subtract(monthAgo, 'months').add(1, 'days').format('YYYY-MM-DD');
  }

  return moment().format('YYYY-MM-DD');
};

export const getViewingDateTimeInfo = (from?: string, to?: string, allDay?: number): string => {
  if (allDay === 1) {
    return t('all_day');
  }
  if (from && !to) {
    return formatDate(from, 'LT');
  }
  if (from && to) {
    return `${formatDate(from, 'LT')} - ${formatDate(to, 'LT')}`;
  }
  return '';
};

function getFirstDayOfQuarterMonth() {
  return moment().quarter(moment().quarter()).startOf('quarter').format('YYYY-MM-DD');
}

export const getDaysCountSinceQuarterStart = () => {
  const today = moment().format('YYYY-MM-DD');

  return moment(today.split('-')).diff(getFirstDayOfQuarterMonth().split('-'), 'days');
};

export const getDaysSince = (date: moment.MomentInput): number | null => {
  const target = moment.utc(date).startOf('day');

  if (!target.isValid()) {
    return null;
  }

  return moment.utc().startOf('day').diff(target, 'days');
};

export const isEndTimeBeforeStartTime = (date: MaterialDate, startTime: string, endTime: string) => {
  return !moment(date)
    .add(moment.duration(startTime))
    .isBefore(moment(date).add(moment.duration(endTime)));
};

export interface UseCurrentDateResult {
  date: number;
  month: number;
  year: number;
}

export const getCurrentDate = (): UseCurrentDateResult => {
  const now = new Date();

  return {
    date: now.getDate(),
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  };
};

export const getMomentDate = (timestamp?: moment.MomentInput): moment.Moment | null => {
  // First attempt to parse as a Unix timestamp
  if (typeof timestamp === 'number' && timestamp) {
    const unixMomentDate = moment.unix(timestamp);

    if (unixMomentDate.isValid()) {
      return unixMomentDate;
    }
  }

  // If timestamp is not a number or the Unix timestamp was invalid, try as a string
  if (typeof timestamp !== 'undefined') {
    const stringMomentDate = moment(timestamp);
    if (stringMomentDate.isValid()) {
      return stringMomentDate;
    }
  }

  return null;
};

interface PrettyDateOptions {
  shortMonth?: boolean;
}

export const getPrettyDate = (timestamp: string | number, options: PrettyDateOptions = { shortMonth: false }) => {
  const momentDate = getMomentDate(timestamp);

  // Handle empty / invalid date
  if (momentDate === null || !momentDate.isValid()) {
    return { date: '', tooltip: '' };
  }

  const isToday = momentDate.isSame(moment(), 'day');
  const isThisYear = momentDate.isSame(moment(), 'year');
  const monthFormat = options.shortMonth ? 'MMM' : 'MMMM';

  let textFormat = `Do ${monthFormat} YYYY`;
  if (isToday) {
    textFormat = 'h:mma';
  } else if (isThisYear) {
    textFormat = `Do ${monthFormat}`;
  }

  return { date: momentDate.format(textFormat), tooltip: isToday ? '' : momentDate.format('LLLL') };
};

type DateRangeState = {
  from: moment.Moment;
  to: moment.Moment;
  sameDay: boolean;
  sameTime: boolean;
};

/**
 * Generates a state object for a date range.
 *
 * @param from - The start date of the range in ISO 8601 format.
 * @param to - The end date of the range in ISO 8601 format.
 * @returns An object containing the start and end dates as moment objects,
 *          and boolean values indicating whether the start and end dates are the same day or the same time.
 */

export const getDateRangeState = (from: string, to: string): DateRangeState => {
  const fromDate = moment(from);
  const toDate = moment(to);

  return {
    from: fromDate,
    to: toDate,
    sameDay: moment(fromDate).isSame(toDate, 'day'),
    sameTime: moment(fromDate).isSame(toDate),
  };
};

export const formatTimeText = (from: string, to: string) =>
  `${formatDate(from, 'ddd, DD MMM YYYY, hh:mm a')} - ${formatDate(to, 'hh:mm a')}`;

export const formatTimestamp = (timestamp: moment.MomentInput, format = 'Do MMM YYYY') => {
  const momentDate = getMomentDate(timestamp);

  if (momentDate === null || !momentDate.isValid()) {
    return '';
  }

  return momentDate.format(format);
};

// Strict: the text must be a whole, real date in exactly this format, so half-typed text such as 16/10/202_ fails
export const isDateTextValid = (text: string, format: string): boolean => moment(text, format, true).isValid();

interface FormatTimeAgoOptions {
  timestamp: string | number;
  // Configuration options
  includeTime?: boolean;
  shortTimeFormat?: boolean;
}

export const formatTimeAgo = ({
  timestamp,
  // Configuration options
  includeTime = false,
  shortTimeFormat = true,
}: FormatTimeAgoOptions): string => {
  const momentDate = getMomentDate(timestamp);

  // Handle empty / invalid date
  if (momentDate === null || !momentDate.isValid()) {
    return '';
  }

  const now = moment();

  // Calculate time difference in minutes
  const secondsAgo = now.diff(momentDate, 'seconds');
  const minutesAgo = now.diff(momentDate, 'minutes');
  const hoursAgo = now.diff(momentDate, 'hours');

  // -23 hours 59 minutes to -1 hour
  if (hoursAgo > -24 && hoursAgo <= -1) {
    return shortTimeFormat ? `${-hoursAgo}h` : pluralText(-hoursAgo, ' hour', ' hours');
  }

  // -59 minutes 59 seconds to -1 minute
  if (minutesAgo > -60 && minutesAgo <= -1) {
    return shortTimeFormat ? `${-minutesAgo}m` : pluralText(-minutesAgo, ' minute', ' minutes');
  }

  // -59 seconds to 0 seconds
  if (secondsAgo > -60 && secondsAgo < 0) {
    return shortTimeFormat ? `${-secondsAgo}s` : pluralText(-secondsAgo, ' second', ' seconds');
  }

  // 0 seconds - 10 seconds
  if (secondsAgo >= 0 && secondsAgo < 10) {
    return 'Just now';
  }

  // 0 seconds to 59 seconds
  if (secondsAgo >= 10 && secondsAgo < 60) {
    return shortTimeFormat ? `${secondsAgo}s ago` : pluralText(secondsAgo, ' second', ' seconds');
  }

  // 1 minute to 59 minutes 59 seconds
  if (minutesAgo >= 1 && minutesAgo < 60) {
    return shortTimeFormat ? `${minutesAgo}m ago` : pluralText(minutesAgo, ' minute', ' minutes');
  }

  // 1 hour to 23 hours 59 minutes
  if (hoursAgo >= 1 && hoursAgo < 24) {
    return shortTimeFormat ? `${hoursAgo}h ago` : pluralText(hoursAgo, ' hour', ' hours');
  }

  return formatTimestamp(timestamp, includeTime ? 'Do MMM YYYY, h:mm a' : 'Do MMM YYYY');
};

/**
 * Converts a time period option to a date range
 * @param option Time period option (e.g. '7days', 'thisMonth', 'q1-2023')
 * @returns Object with startDate and endDate as formatted strings
 */
export const getDateRangeFromOption = (option: string): { startDate: string; endDate: string } => {
  const today = new Date();

  switch (option) {
    case '7days':
      return {
        startDate: formatDate(moment(today).subtract(7, 'days'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case '30days':
      return {
        startDate: formatDate(moment(today).subtract(30, 'days'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case '60days':
      return {
        startDate: formatDate(moment(today).subtract(60, 'days'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case '90days':
      return {
        startDate: formatDate(moment(today).subtract(90, 'days'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case 'thisWeek':
      return {
        startDate: formatDate(moment(today).startOf('week'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case 'thisMonth':
      return {
        startDate: formatDate(moment(today).startOf('month'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case 'thisQuarter':
      return {
        startDate: formatDate(moment(today).startOf('quarter'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case 'thisYear':
      return {
        startDate: formatDate(moment(today).startOf('year'), 'YYYY-MM-DD'),
        endDate: formatDate(today, 'YYYY-MM-DD'),
      };
    case 'lastQuarter':
      return {
        startDate: formatDate(moment(today).subtract(1, 'quarter').startOf('quarter'), 'YYYY-MM-DD'),
        endDate: formatDate(moment(today).subtract(1, 'quarter').endOf('quarter'), 'YYYY-MM-DD'),
      };
    case 'lastYear':
      return {
        startDate: formatDate(moment(today).subtract(1, 'year').startOf('year'), 'YYYY-MM-DD'),
        endDate: formatDate(moment(today).subtract(1, 'year').endOf('year'), 'YYYY-MM-DD'),
      };
    default:
      return {
        startDate: '',
        endDate: '',
      };
  }
};

/**
 * Generates reminder day options for requirement reminders
 * @returns Array of reminder day options with id and label
 */
export const getReminderDaysOptions = () => {
  // Mirrors the backend Constants::getRequirementReminderDayOptions keys (0 plus 1-7 days), which
  // the email endpoints validate against with in_array.
  const reminderDayValues = [0, 1, 2, 3, 4, 5, 6, 7];

  return reminderDayValues.map((optionValue) => {
    if (optionValue === 0) {
      return { id: '0', label: t('do_not_remind_me') };
    } else if (optionValue === 1) {
      return { id: '1', label: t('tomorrow') };
    } else {
      // Generate dynamic date labels using moment's built-in ordinal formatting
      const futureDate = moment().add(optionValue, 'days');
      return {
        id: optionValue.toString(),
        label: futureDate.format('dddd Do'),
      };
    }
  });
};

/**
 * Checks if a date falls within British Summer Time (BST).
 * BST runs from last Sunday of March 01:00 GMT to last Sunday of October 02:00 BST.
 *
 * @param date - The date to check
 * @returns true if the date is in BST period
 */
const isInBST = (date: Date): boolean => {
  const year = date.getFullYear();

  const getLastSundayOfMonth = (year: number, month: number): Date => {
    const lastDay = new Date(year, month + 1, 0);
    const day = lastDay.getDay();
    const lastSunday = new Date(year, month, lastDay.getDate() - day);
    return lastSunday;
  };

  const bstStart = getLastSundayOfMonth(year, 2);
  bstStart.setHours(1, 0, 0, 0);

  const bstEnd = getLastSundayOfMonth(year, 9);
  bstEnd.setHours(1, 0, 0, 0);

  return date >= bstStart && date < bstEnd;
};

/**
 * Parses a date string as Europe/London timezone and returns a UTC timestamp for comparison.
 * Handles both Laravel format ("YYYY-MM-DD HH:mm:ss" in Europe/London) and ISO 8601 format (UTC).
 *
 * Backend times are always in Europe/London timezone, which observes:
 * - GMT (UTC+0) during winter
 * - BST (UTC+1) during summer (last Sunday of March to last Sunday of October)
 *
 * @param dateString - Date string to parse
 * @returns Unix timestamp in milliseconds, or null if invalid
 */
export const parseUTCTimestamp = (dateString: string | boolean | undefined | null): number | null => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const laravelFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

  if (laravelFormat.test(dateString)) {
    const parts = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
    if (!parts || parts.length < 7) {
      return null;
    }

    const year = parts[1];
    const month = parts[2];
    const day = parts[3];
    const hour = parts[4];
    const minute = parts[5];
    const second = parts[6];

    if (!year || !month || !day || !hour || !minute || !second) {
      return null;
    }

    const checkDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), 12, 0, 0);

    if (isNaN(checkDate.getTime())) {
      return null;
    }

    const offset = isInBST(checkDate) ? 1 : 0;

    const utcDate = Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10) - offset,
      parseInt(minute, 10),
      parseInt(second, 10)
    );

    return utcDate;
  }

  const momentDate = moment.utc(dateString);
  return momentDate.isValid() ? momentDate.valueOf() : null;
};
